import React, { useEffect } from 'react';
import { Tabs } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useBooking } from '../../../../../../../contexts/ticketBookingContext/TicketBookingContext';
import CompleteStatementBookingNavigationTabItems
  from '../bookingTypeNavigationTabItems/CompleteStatementBookingNavigationTabItems';
import autoPassengersFormSet from '../../../../../../../../utils/functions/booking/autoPassengersFormSet';
import reserveSeatsWithAdditionalServices
  from '../../../../../../../../utils/functions/booking/reserveSeatsWithAdditionalServices';
import purchaseBookedTickets from '../../../../../../../../utils/functions/booking/purchaseBookedTickets';
import BookingNavigationButtons from '../BookingNavigationButtons';
import generateInvoiceBooking from '../../../../../../../../utils/functions/booking/generateInvoiceBooking';
import useTranslation from '../../../../../../../hooks/translation/useTranslation';
import ChosenRideDetails
  from '../../../../../../components/drawers/ticketSearchDrawers/ticketSearchFormDrawer/components/ChosenRideDetails';
import checkPassengers from '../../../../../../../../utils/functions/booking/checkPassengers';

function CompleteStatementBookingNavigation({ outline = true }) {
  const { trans } = useTranslation();
  const [searchQuery] = useSearchParams();
  const {
    configInfo,
  } = useTranslation();
  const {
    activeStep,
    departurePassengersForm,
    bookingData,
    setActiveStep,
    returnPassengersForm,
    services,
    setStatement,
    paymentMethodForm,
    setIsNextDisabled,
  } = useBooking();

  const isReturnWay = Boolean(bookingData?.return?.rideId);
  const lastStep = isReturnWay ? 3 : 2;

  useEffect(() => {
    const setPassengersForms = async ({ passengersForm, statementRide }) => {
      await autoPassengersFormSet({
        passengersForm,
        statementRide,
      });
    };
    if (bookingData?.departure) {
      setPassengersForms({
        passengersForm: departurePassengersForm,
        statementRide: bookingData?.departure,
      });
    }
    if (bookingData?.return?.rideId) {
      setPassengersForms({
        passengersForm: returnPassengersForm,
        statementRide: bookingData?.return,
      });
    }
  }, [bookingData?.id]);

  const handleSubmit = async () => {
    if (bookingData?.invoice) {
      await generateInvoiceBooking(bookingData.id, bookingData?.guid);
    } else {
      await purchaseBookedTickets({
        id: bookingData.id,
        guid: searchQuery.get('guid'),
        countryCode: configInfo.countryCode,
        payment_card: paymentMethodForm.getFieldValue('payment_card'),
        payment_bank: paymentMethodForm.getFieldValue('payment_bank'),
        trans,
        setIsNextDisabled,
      });
    }
  };

  // Define handlers for each step in the process
  const nextStepHandlers = {
    1: async () => {
      await departurePassengersForm.validateFields();
      if (!isReturnWay) {
        checkPassengers({
          form: departurePassengersForm,
          onSuccess: async () => {
            await reserveSeatsWithAdditionalServices({
              departurePassengersForm,
              bookingData,
              statement: {
                id: bookingData.id,
                statementRides: [bookingData.departure, bookingData?.return],
                statementGuid: searchQuery.get('guid'),
              },
              services,
              setStatement,
              nextStep: () => {
                setActiveStep(2);
              },
            });
          },
          trans,
        });
      } else {
        checkPassengers({
          form: departurePassengersForm,
          onSuccess: async () => {
            setActiveStep(2);
          },
          trans,
        });
      }
    },
    2: async () => {
      if (isReturnWay) {
        await returnPassengersForm.validateFields();
        await reserveSeatsWithAdditionalServices({
          departurePassengersForm,
          returnPassengersForm,
          bookingData,
          statement: {
            id: bookingData.id,
            statementRides: [bookingData.departure, bookingData?.return],
            statementGuid: searchQuery.get('guid'),
          },
          services,
          setStatement,
          nextStep: () => setActiveStep(3),
        });
      } else {
        handleSubmit();
      }
    },
    3: async () => {
      handleSubmit();
    },
  };

  const handleNextStep = () => {
    nextStepHandlers[activeStep]();
  };

  const handlePreviousStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <div className={`flex flex-col grow p-8 rounded-2xl bg-white ${outline && 'outline-border'} !sticky top-[300px]`}>
      {(bookingData?.departure || bookingData?.return) && (
      <div className="mb-4 hidden lg:block">
        {bookingData?.departure && <ChosenRideDetails chosenRide={bookingData?.departure} />}
        {bookingData?.return && <ChosenRideDetails chosenRide={bookingData?.return} />}
      </div>
      )}
      <Tabs className="no-tablist" items={CompleteStatementBookingNavigationTabItems()} activeKey={activeStep} />
      <BookingNavigationButtons
        handlePreviousStep={handlePreviousStep}
        handleNextStep={handleNextStep}
        activeStep={activeStep}
        isNextDisabled={false}
        lastStep={lastStep}
      />
    </div>
  );
}

export default CompleteStatementBookingNavigation;
